import {Content, ResponsiveTable, Typography, useBreakPoints} from '@hconnect/uikit'
import {PageHeader} from '@hm/fundamental'
import AddIcon from '@mui/icons-material/Add'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import ModeEditOutlineIcon from '@mui/icons-material/ModeEditOutline'
import {Alert, Box, Button, Grid, IconButton, Paper} from '@mui/material'
import React, {useMemo, useState} from 'react'
import {useHistory} from 'react-router-dom'

import {useTranslation} from '../../../../hooks/useTranslation'
import {useAllowedHConnectConfigCountries} from '../hooks/useAllowedHConnectConfigCountries'

import {DeleteDialog} from './components/SlotManagementConfigurationsDeleteDialog'
import {useSlotManagementConfigsQuery} from './hooks/useSlotManagementConfigsQuery'
import {SlotManagementConfig} from './types/SlotManagementConfig'

export const SlotManagementConfigurationList = () => {
  const {t} = useTranslation()
  const history = useHistory()
  const screenSizes = useBreakPoints()
  const isMobile = ['xs', 'sm'].includes(screenSizes)

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [configurationToDelete, setConfigurationToDelete] = useState<string | null>(null)

  const handleCreateConfiguration = () => {
    history.push('/configuration/hconnect/slot-management/new')
  }

  const handleOpenDeleteDialog = (configurationId: string) => {
    setConfigurationToDelete(configurationId)
    setDeleteDialogOpen(true)
  }

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false)
    setConfigurationToDelete(null)
  }

  const handleRowAction: HandleConfigurationAction = (configurationId, action) => {
    switch (action) {
      case 'edit':
        history.push(`/configuration/hconnect/slot-management/${configurationId}/edit`)
        break
      case 'copy':
        history.push(`/configuration/hconnect/slot-management/${configurationId}/copy`)
        break
      case 'delete':
        handleOpenDeleteDialog(configurationId)
        break
      default:
        console.error('Unknown action:', action)
    }
  }

  const slotManagementConfigQueryInfo = useSlotManagementConfigsQuery()
  const configurableHConnectCountries = useAllowedHConnectConfigCountries()

  const filteredAndSortedData = useMemo(() => {
    return (
      slotManagementConfigQueryInfo.data
        ?.filter((c) => configurableHConnectCountries.includes(c.countryCode))
        .sort((a, b) => {
          // Sort by country, then business line, then orgUnitId
          if (a.countryCode !== b.countryCode) {
            return a.countryCode.localeCompare(b.countryCode)
          }
          if (a.businessLine !== b.businessLine) {
            return a.businessLine.localeCompare(b.businessLine)
          }
          return (a.orgUnitId || '').localeCompare(b.orgUnitId || '')
        }) ?? []
    )
  }, [slotManagementConfigQueryInfo.data, configurableHConnectCountries])

  return (
    <Content>
      <div className="[&_*]:!text-white">
        <PageHeader
          data-cy="page-slot-management-configurations"
          className="pl-0 pr-0 md:pl-0 md:pr-0"
          aside={
            <Button
              data-test-id="create-configuration-button"
              variant="contained"
              onClick={handleCreateConfiguration}
              startIcon={<AddIcon />}
              style={{alignContent: 'center', margin: '0 auto', marginTop: isMobile ? '12px' : 0}}
            >
              {t('configurations.hconnect.slotManagement.createConfigurationButton')}
            </Button>
          }
        >
          {t('configurations.hconnect.slotManagement.title')}
        </PageHeader>
      </div>

      <Grid item md={12} sm={12} xs={0}>
        <Paper elevation={4} style={{padding: isMobile ? '4px 8px' : '26px 36px'}}>
          <ResponsiveTable
            keyField="id"
            columns={SlotManagementConfigurationsRows(handleRowAction)}
            rows={slotManagementConfigQueryInfo.isError ? [] : filteredAndSortedData}
            emptyMessage={
              slotManagementConfigQueryInfo.isError ? (
                <Alert severity="error">
                  {t('configurations.hconnect.slotManagement.errorDataFetching')}
                </Alert>
              ) : (
                ''
              )
            }
            tableHeight="70vh"
            onSort={() => ({})}
            loading={slotManagementConfigQueryInfo.isLoading}
            enableHorizontalScroll={true}
            stickyHeader={true}
            enablePinning={true}
            isMobile={isMobile}
            isRowSelectable={isMobile ? undefined : () => true}
          />
        </Paper>
      </Grid>
      <DeleteDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        configurationId={configurationToDelete ?? ''}
        confirmMessage={t(
          'configurations.hconnect.slotManagement.configurationList.deleteDialogMessage'
        )}
        dialogTitle={t(
          'configurations.hconnect.slotManagement.configurationList.deleteDialogTitle'
        )}
      />
    </Content>
  )
}

const SlotManagementConfigurationsRows = (
  handleAction: HandleConfigurationAction
): {
  field: string
  headerName: string
  renderCell: (row: SlotManagementConfig) => JSX.Element
}[] => {
  const {t} = useTranslation()

  return [
    {
      field: 'action',
      headerName: t('configurations.hconnect.slotManagement.configurationList.action'),
      renderCell: (row: SlotManagementConfig) => (
        <Box
          style={{
            display: 'flex',
            justifyContent: 'start',
            alignItems: 'center'
          }}
        >
          <IconButton
            data-test-id={`action-edit-slot-management-${row.id}`}
            onClick={() => handleAction(row.id, 'edit')}
            aria-label="edit"
            style={{boxShadow: 'none'}}
          >
            <ModeEditOutlineIcon style={{color: '#016AD4'}} />
          </IconButton>
          <IconButton
            data-test-id={`action-copy-slot-management-${row.id}`}
            onClick={() => handleAction(row.id, 'copy')}
            style={{boxShadow: 'none'}}
            aria-label="copy"
          >
            <ContentCopyIcon style={{color: '#016AD4'}} />
          </IconButton>
          <IconButton
            data-test-id={`action-delete-slot-management-${row.id}`}
            onClick={() => handleAction(row.id, 'delete')}
            style={{boxShadow: 'none'}}
            aria-label="delete"
          >
            <DeleteIcon style={{color: '#016AD4', fontSize: '24px'}} />
          </IconButton>
        </Box>
      )
    },
    {
      field: 'country',
      headerName: t('configurations.hconnect.slotManagement.configurationList.country'),
      renderCell: (row) => (
        <Typography data-test-id={`cell-country-${row.id}`}>{row.countryCode}</Typography>
      )
    },
    {
      field: 'businessLine',
      headerName: t('configurations.hconnect.slotManagement.configurationList.businessLine'),
      renderCell: (row) => <Typography>{row.businessLine}</Typography>
    },
    {
      field: 'orgUnitId',
      headerName: t('configurations.hconnect.slotManagement.configurationList.orgUnitId'),
      renderCell: (row) => <Typography>{row.orgUnitId || '-'}</Typography>
    },
    {
      field: 'dispatchGroup',
      headerName: t('configurations.hconnect.slotManagement.configurationList.dispatchGroup'),
      renderCell: (row) => <Typography>{row.dispatchGroup || '-'}</Typography>
    },
    {
      field: 'plantNumber',
      headerName: t('configurations.hconnect.slotManagement.configurationList.plantNumber'),
      renderCell: (row) => <Typography>{row.plantNumber || '-'}</Typography>
    }
  ]
}

type HandleConfigurationAction = (
  configurationId: string,
  action: 'edit' | 'copy' | 'delete'
) => void
